import { TradeVenue } from '@/types/generated/common/types'
import { OauthConfig } from '@/types/generated/public_api'

/** postMessage contract between the OAuth popup callback page and the opener. */
export const VENUE_OAUTH_MESSAGE_SOURCE = 'venue-oauth-callback'

export interface VenueOauthCallbackMessage {
  source: typeof VENUE_OAUTH_MESSAGE_SOURCE
  code: string
  state: string
  error: string
}

export function isVenueOauthCallbackMessage(data: unknown): data is VenueOauthCallbackMessage {
  return (
    typeof data === 'object' &&
    data !== null &&
    (data as VenueOauthCallbackMessage).source === VENUE_OAUTH_MESSAGE_SOURCE
  )
}

/**
 * localStorage key the popup writes its result to, keyed by state so
 * concurrent OAuth attempts in different tabs never cross-wire. This is the
 * primary, reliable relay path — see `relayOauthCallbackMessage` for why.
 */
function oauthStorageKey(state: string): string {
  return `${VENUE_OAUTH_MESSAGE_SOURCE}:${state}`
}

/**
 * Relay the callback message from the OAuth popup to the opener tab.
 *
 * localStorage is the primary, authoritative path: confirmed by testing
 * (2026-07) that on this app, once the popup navigates out to the venue's
 * domain and back, neither `window.opener.postMessage` NOR same-origin
 * BroadcastChannel reliably reach the opener anymore — a plain `about:blank`
 * popup that never leaves our origin delivers fine over BroadcastChannel,
 * but the real OAuth round-trip through the venue's domain does not. That
 * matches Chromium's Cross-Origin-Opener-Policy browsing-context-group swap
 * breaking more than just `window.opener` in practice. localStorage isn't a
 * live IPC channel — it's a durable, disk-backed write the opener polls for
 * — so it isn't subject to that failure mode. postMessage/BroadcastChannel
 * are kept as opportunistic, harmless bonuses for browsers/venues where the
 * live channels do survive.
 */
export function relayOauthCallbackMessage(message: VenueOauthCallbackMessage): void {
  try {
    localStorage.setItem(oauthStorageKey(message.state), JSON.stringify(message))
  } catch {
    // Ignore — private-browsing storage restrictions etc. The other channels
    // below are still attempted.
  }
  if (window.opener) {
    try {
      window.opener.postMessage(message, window.location.origin)
    } catch {
      // Ignore — see block comment above.
    }
  }
  if (typeof BroadcastChannel !== 'undefined') {
    try {
      const channel = new BroadcastChannel(VENUE_OAUTH_MESSAGE_SOURCE)
      channel.postMessage(message)
      channel.close()
    } catch {
      // Ignore — see block comment above.
    }
  }
}

/** Frontend-owned callback path, by venue. Must byte-match the backend allowlist
 *  and the value registered with the venue. */
const VENUE_CALLBACK_PATH: Partial<Record<TradeVenue, string>> = {
  [TradeVenue.TRADE_VENUE_DERIBIT]: '/api/oauth/deribit',
  [TradeVenue.TRADE_VENUE_BYBIT]: '/api/oauth/bybit',
}

/** `/api/oauth/<venue>` callback URL. Bybit registers and matches a full
 *  `https://` URL; Deribit registers scheme-less (and signs the byte-identical
 *  value into its token-exchange body). Must match the backend allowlist exactly. */
export function venueCallbackUrl(venue: TradeVenue): string {
  const path = VENUE_CALLBACK_PATH[venue]
  if (!path) throw new Error('OAuth is not supported for this venue.')
  if (venue === TradeVenue.TRADE_VENUE_BYBIT) return `${window.location.origin}${path}`
  return `${window.location.host}${path}`
}

/**
 * Build a venue authorize URL from its OauthConfig + the frontend-owned redirect URI
 * and minted state nonce. scope/response_type are sent only when the config provides
 * them (both Deribit and Bybit set them; an absent config field is simply omitted).
 * Every value is URL-encoded.
 */
export function buildAuthorizeUrl(oauth: OauthConfig, redirectUri: string, state: string): string {
  const url = new URL(oauth.authorizeUrl)
  url.searchParams.set('client_id', oauth.clientId)
  url.searchParams.set('redirect_uri', redirectUri)
  if (oauth.scope) url.searchParams.set('scope', oauth.scope)
  if (oauth.responseType) url.searchParams.set('response_type', oauth.responseType)
  url.searchParams.set('state', state)
  return url.toString()
}

/** Read the grant from a callback URL query string (code lives in the query, not the fragment). */
export function parseOauthCallbackParams(search: string): {
  code: string
  state: string
  error: string
} {
  const params = new URLSearchParams(search)
  return {
    code: params.get('code') ?? '',
    state: params.get('state') ?? '',
    error: params.get('error') ?? params.get('error_description') ?? '',
  }
}

/** The minted state nonce expires server-side after 5 minutes. */
const OAUTH_TIMEOUT_MS = 5 * 60 * 1000

/**
 * Waits for the OAuth popup to bounce back through /api/oauth/[venue].
 * Resolves with the one-time code once the callback page relays it; rejects
 * if the venue reports an error, the echoed state doesn't match, or the
 * 5-minute state lifetime elapses.
 *
 * Deliberately does NOT poll `popup.closed` to fast-fail on an assumed
 * user-cancel: confirmed by testing (2026-07) that on this app, `popup.closed`
 * read from the opener can report `true` within ~1 second of opening — while
 * the popup is still genuinely open and mid-flow — almost certainly because
 * Deribit's own Cross-Origin-Opener-Policy swaps the popup into a new
 * browsing-context-group the moment it navigates there, disowning the
 * opener's WindowProxy handle. Trusting that signal was tearing down every
 * listener (including the localStorage poll below) long before the popup
 * could ever finish and relay its result. The 5-minute timeout below is the
 * only abandonment fallback; a user who closes the popup without completing
 * sees that timeout rather than an immediate error.
 */
export function waitForOauthCallback(
  expectedState: string
): Promise<{ code: string; state: string }> {
  return new Promise((resolve, reject) => {
    let storagePoll = 0
    let timeout = 0
    const channel =
      typeof BroadcastChannel !== 'undefined'
        ? new BroadcastChannel(VENUE_OAUTH_MESSAGE_SOURCE)
        : null

    const cleanup = () => {
      window.removeEventListener('message', onMessage)
      channel?.removeEventListener('message', onBroadcast)
      channel?.close()
      window.clearInterval(storagePoll)
      window.clearTimeout(timeout)
      try {
        localStorage.removeItem(oauthStorageKey(expectedState))
      } catch {
        // Ignore — see relayOauthCallbackMessage's block comment.
      }
    }

    const handleCallback = (data: unknown) => {
      if (!isVenueOauthCallbackMessage(data)) return
      cleanup()

      const { code, state, error } = data
      if (error) {
        reject(new Error(`Authorization failed: ${error}`))
      } else if (!code || state !== expectedState) {
        reject(new Error('Authorization returned an invalid response. Please try again.'))
      } else {
        resolve({ code, state })
      }
    }

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return
      handleCallback(event.data)
    }
    const onBroadcast = (event: MessageEvent) => handleCallback(event.data)

    window.addEventListener('message', onMessage)
    channel?.addEventListener('message', onBroadcast)

    // Primary path — see relayOauthCallbackMessage's block comment for why
    // postMessage/BroadcastChannel alone aren't reliable here. localStorage
    // is a plain write the popup makes before closing; poll for it rather
    // than depend on a `storage` event (which, per the same investigation,
    // isn't worth trusting either once the popup has round-tripped through
    // the venue's origin).
    const storageKey = oauthStorageKey(expectedState)
    storagePoll = window.setInterval(() => {
      let raw: string | null = null
      try {
        raw = localStorage.getItem(storageKey)
      } catch {
        return
      }
      if (raw === null) return
      try {
        handleCallback(JSON.parse(raw))
      } catch {
        // Malformed value — ignore and keep polling/let other paths resolve.
      }
    }, 250)

    timeout = window.setTimeout(() => {
      cleanup()
      reject(new Error('Authorization timed out. Please try again.'))
    }, OAUTH_TIMEOUT_MS)
  })
}
